import { Injectable } from '@angular/core';

export type A11yArrowNavigationDirection = 1 | -1;

@Injectable({
  providedIn: 'root'
})
export class A11yKeyboardService {
  public isActivationKey(event: KeyboardEvent): boolean {
    return event.key === 'Enter' || event.key === ' ';
  }

  public activateOnEnterOrSpace(event: KeyboardEvent, action: () => void): void {
    if (!this.isActivationKey(event)) {
      return;
    }

    event.preventDefault();
    action();
  }

  public handleArrowNavigation(event: KeyboardEvent, action: (direction: A11yArrowNavigationDirection) => void): boolean {
    const direction = this.getArrowNavigationDirection(event);

    if (direction === null) {
      return false;
    }

    event.preventDefault();
    action(direction);

    return true;
  }

  public getArrowNavigationDirection(event: KeyboardEvent): A11yArrowNavigationDirection | null {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      return 1;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      return -1;
    }

    return null;
  }

  public focusNext(items: HTMLElement[], currentIndex: number): void {
    if (!items.length) {
      return;
    }

    items[(currentIndex + 1) % items.length].focus();
  }

  public focusPrevious(items: HTMLElement[], currentIndex: number): void {
    if (!items.length) {
      return;
    }

    items[(currentIndex - 1 + items.length) % items.length].focus();
  }

  /**
   * Navigate through a listbox with arrow keys and handle selection/close
   * @param event The keyboard event
   * @param itemCount Number of items in the listbox
   * @param currentIndex Current focus index (-1 if none)
   * @param onNavigate Callback to update the focus index
   * @param onSelect Callback when Enter is pressed with valid selection
   * @param onClose Callback when Escape or Tab is pressed
   */
  public handleListboxNavigation(
    event: KeyboardEvent,
    itemCount: number,
    currentIndex: number,
    onNavigate: (newIndex: number) => void,
    onSelect: () => void,
    onClose: () => void
  ): void {
    if (itemCount === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        onNavigate(currentIndex < 0 ? 0 : Math.min(currentIndex + 1, itemCount - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        onNavigate(currentIndex < 0 ? itemCount - 1 : Math.max(currentIndex - 1, 0));
        break;
      case 'Enter':
        if (currentIndex >= 0 && currentIndex < itemCount) {
          event.preventDefault();
          onSelect();
        }
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'Tab':
        // Allow Tab to close the listbox and move focus naturally
        onClose();
        // Don't preventDefault() to allow natural tab navigation
        break;
    }
  }
}
